import { isMobileBrowser } from '../base/environment/utils';
import { VIDEO_SPACE_MIN_SIZE } from '../video-layout/constants';
import { DEFAULT_FLOATING_HEIGHT, DEFAULT_FLOATING_TOP, DEFAULT_FLOATING_WIDTH, DEFAULT_SIDE_PANEL_WIDTH, FLOATING_Z_INDEX_BASE, SIDE_PANEL_FEATURE_KEY, SIDE_PANEL_POPOUT_MIN_WIDTH } from './constants';
/**
 * 侧栏默认状态（与 reducer DEFAULT_STATE 对齐；用于缺省/半成品 rehydrate 防护）。
 */
const SIDE_PANEL_STATE_FALLBACK = {
    activeTab: null,
    floatingGeometry: {},
    floatingTabs: [],
    isResizing: false,
    nextFloatingZIndex: FLOATING_Z_INDEX_BASE,
    openTabs: [],
    width: {
        current: DEFAULT_SIDE_PANEL_WIDTH,
        userSet: null
    }
};
/**
 * Returns the side panel feature state with a safe fallback.
 * 持久化可能只恢复 width；必须补齐 openTabs/floatingTabs，避免 .length 空引用黑屏。
 *
 * @param {IReduxState} state - Redux state.
 * @returns {ISidePanelState}
 */
export function getSidePanelState(state) {
    const raw = state[SIDE_PANEL_FEATURE_KEY];
    if (!raw) {
        return SIDE_PANEL_STATE_FALLBACK;
    }
    return {
        ...SIDE_PANEL_STATE_FALLBACK,
        ...raw,
        floatingGeometry: raw.floatingGeometry ?? {},
        floatingTabs: Array.isArray(raw.floatingTabs) ? raw.floatingTabs : [],
        openTabs: Array.isArray(raw.openTabs) ? raw.openTabs : [],
        width: {
            ...SIDE_PANEL_STATE_FALLBACK.width,
            ...(raw.width || {})
        }
    };
}
/**
 * Whether the docked side panel host should be visible.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {boolean}
 */
export function isSidePanelOpen(state) {
    return getSidePanelState(state).openTabs.length > 0;
}
/**
 * Whether a tab is open (docked or floating).
 *
 * @param {IReduxState} state - Redux state.
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {boolean}
 */
export function isSidePanelTabOpen(state, tabId) {
    const { openTabs, floatingTabs } = getSidePanelState(state);
    return openTabs.includes(tabId) || floatingTabs.includes(tabId);
}
/**
 * Whether a tab is currently floating.
 *
 * @param {IReduxState} state - Redux state.
 * @param {SidePanelTabId} tabId - Tab id.
 * @returns {boolean}
 */
export function isSidePanelTabFloating(state, tabId) {
    return getSidePanelState(state).floatingTabs.includes(tabId);
}
/**
 * Current docked side panel width (0 when no docked tabs).
 *
 * @param {IReduxState} state - Redux state.
 * @returns {number}
 */
export function getSidePanelWidth(state) {
    const sidePanel = getSidePanelState(state);
    if (!sidePanel.openTabs.length) {
        return 0;
    }
    return sidePanel.width?.current ?? DEFAULT_SIDE_PANEL_WIDTH;
}
/**
 * Maximum width available for the docked side panel.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {number}
 */
export function getSidePanelMaxSize(state) {
    const { clientWidth } = state['features/base/responsive-ui'];
    return Math.max(clientWidth - VIDEO_SPACE_MIN_SIZE, 0);
}
/**
 * Whether pop-out is allowed for the current viewport.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {boolean}
 */
export function isSidePanelPopoutEnabled(state) {
    const { clientWidth } = state['features/base/responsive-ui'];
    return clientWidth > SIDE_PANEL_POPOUT_MIN_WIDTH;
}
/**
 * 手机端是否用底部抽屉承载侧栏应用（聊天 / AI纪要 / 参会者等）。
 * 电脑 Web 始终走右侧侧栏（窄屏仍为全屏覆盖，保持原状）。
 *
 * @returns {boolean}
 */
export function shouldUseSidePanelBottomDrawer() {
    return isMobileBrowser();
}
/**
 * Build default floating geometry for a newly popped-out tab.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {IFloatingGeometry}
 */
export function createDefaultFloatingGeometry(state) {
    const { clientWidth, clientHeight } = state['features/base/responsive-ui'];
    const sidePanel = getSidePanelState(state);
    const width = Math.min(DEFAULT_FLOATING_WIDTH, Math.max(280, clientWidth - 48));
    const height = Math.min(DEFAULT_FLOATING_HEIGHT, Math.max(320, clientHeight - 96));
    const x = Math.max(24, Math.round((clientWidth - width) / 2));
    const y = Math.min(DEFAULT_FLOATING_TOP, Math.max(24, clientHeight - height - 24));
    return {
        height,
        width,
        x,
        y,
        zIndex: sidePanel.nextFloatingZIndex
    };
}
